import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, withFreshAccess } from '../api/client'
import { useAccounts } from '../store/accounts'

interface FeedView {
  uri: string
  cid: string
  did: string
  creator: { did: string; handle: string; displayName?: string }
  displayName: string
  description?: string
  avatar?: string
  likeCount?: number
}

interface SavedFeedsPref {
  $type?: string
  items?: { id: string; type: string; value: string; pinned: boolean }[]
  /** Older variant. */
  pinned?: string[]
  saved?: string[]
}

/**
 * Right-side panel showing the active account's pinned Bsky feed
 * generators. Sourced from app.bsky.actor.getPreferences and resolved via
 * app.bsky.feed.getFeedGenerators. Clicking a feed navigates the MAIN
 * column to /feed/<encoded-uri> so the feed becomes the user's active
 * view (instead of expanding inside the right panel).
 */
export function PinnedFeeds() {
  const acc = useAccounts((s) => s.accounts.find((a) => a.id === s.activeId))
  const navigate = useNavigate()
  const [feeds, setFeeds] = useState<FeedView[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function loadPinned() {
    if (!acc) return
    setBusy(true)
    setErr(null)
    try {
      const prefs = await withFreshAccess(acc.id, () =>
        api.xrpc<{ preferences?: SavedFeedsPref[] }>({
          accountId: acc.id,
          method: 'GET',
          path: 'app.bsky.actor.getPreferences',
        }),
      )
      const pinnedUris: string[] = []
      for (const p of prefs.preferences ?? []) {
        if (p.$type === 'app.bsky.actor.defs#savedFeedsPrefV2' && Array.isArray(p.items)) {
          for (const it of p.items) {
            if (it.pinned && it.type === 'feed') pinnedUris.push(it.value)
          }
        } else if (p.$type === 'app.bsky.actor.defs#savedFeedsPref' && Array.isArray(p.pinned)) {
          for (const u of p.pinned) pinnedUris.push(u)
        }
      }
      if (pinnedUris.length === 0) {
        setFeeds([])
        return
      }
      const qs = pinnedUris.map((u) => 'feeds=' + encodeURIComponent(u)).join('&')
      const got = await withFreshAccess(acc.id, () =>
        api.xrpc<{ feeds?: FeedView[] }>({
          accountId: acc.id,
          method: 'GET',
          path: `app.bsky.feed.getFeedGenerators?${qs}`,
        }),
      )
      setFeeds(got.feeds ?? [])
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void loadPinned()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acc?.id])

  function openFeedFn(f: FeedView) {
    navigate(`/feed/${encodeURIComponent(f.uri)}`)
  }

  return (
    <div className="flex h-full flex-col">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-white/10 bg-ink-900/85 px-3 py-2 backdrop-blur-xl">
        <div className="text-[11px] uppercase tracking-widest text-ink-300">
          pinned feeds
          <span className="ml-2 text-ink-500">({feeds.length})</span>
        </div>
        <button
          type="button"
          onClick={loadPinned}
          className="glass-button text-xs"
          disabled={busy}
          title="refresh"
        >
          {busy ? '…' : '⟳'}
        </button>
      </header>

      {err ? (
        <div className="m-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
          {err}
        </div>
      ) : null}

      {!acc ? (
        <div className="p-3 text-xs text-ink-300">sign in to see pinned feeds.</div>
      ) : null}

      {acc && !busy && feeds.length === 0 ? (
        <div className="p-3 text-xs text-ink-300">
          no pinned feeds — pin some on bsky.app and they'll show here.
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto p-2 text-xs">
        <ul className="space-y-1">
          {feeds.map((f) => (
            <li key={f.uri}>
              <button
                type="button"
                onClick={() => openFeedFn(f)}
                className="flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2 text-left hover:bg-white/10"
                title="open feed in main column"
              >
                {f.avatar ? (
                  <img
                    src={f.avatar}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-md object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="h-8 w-8 shrink-0 rounded-md bg-gradient-to-br from-emerald-500/40 to-sky-500/40" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-white">{f.displayName}</div>
                  <div className="truncate text-[10px] text-ink-300">
                    by @{f.creator.handle} · ❤︎ {f.likeCount ?? 0}
                  </div>
                </div>
                <span className="ml-1 text-[10px] text-ink-400">→</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
